/**
 * Confirmation
 *
 * AI Elements-style approval primitives for tool execution workflows.
 *
 * Primary responsibilities:
 * - Render approval request, accepted, and rejected states
 * - Provide compact action slots for chat-native confirmations
 *
 * Dependencies:
 * - components/ui/alert
 * - components/ui/button
 */

'use client'

import {
  createContext,
  type ComponentProps,
  useContext
} from 'react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export type ConfirmationState =
  | 'input-streaming'
  | 'input-available'
  | 'approval-requested'
  | 'approval-responded'
  | 'output-denied'
  | 'output-available'

export interface ConfirmationApproval {
  id?: string
  approved?: boolean | null
}

interface ConfirmationContextValue {
  approval: ConfirmationApproval
  state: ConfirmationState
}

const ConfirmationContext = createContext<ConfirmationContextValue | null>(null)

function useConfirmation() {
  const context = useContext(ConfirmationContext)

  if (!context) {
    throw new Error('Confirmation components must be used within Confirmation')
  }

  return context
}

function isAccepted(state: ConfirmationState, approval: ConfirmationApproval) {
  return approval.approved === true || state === 'output-available'
}

function isRejected(state: ConfirmationState, approval: ConfirmationApproval) {
  return (
    state === 'output-denied' ||
    (state === 'approval-responded' && approval.approved === false)
  )
}

export interface ConfirmationProps extends ComponentProps<typeof Alert> {
  approval?: ConfirmationApproval | null
  state: ConfirmationState
}

export function Confirmation({
  approval,
  state,
  className,
  children,
  ...props
}: ConfirmationProps) {
  if (!approval || state === 'input-streaming' || state === 'input-available') {
    return null
  }

  return (
    <ConfirmationContext.Provider value={{ approval, state }}>
      <Alert
        className={cn('not-prose border-border/70 bg-muted/20 p-3', className)}
        variant={isRejected(state, approval) ? 'destructive' : 'default'}
        {...props}
      >
        {children}
      </Alert>
    </ConfirmationContext.Provider>
  )
}

export function ConfirmationTitle({
  className,
  ...props
}: ComponentProps<typeof AlertTitle>) {
  useConfirmation()

  return <AlertTitle className={cn('text-sm', className)} {...props} />
}

export function ConfirmationRequest({
  className,
  ...props
}: ComponentProps<typeof AlertDescription>) {
  const { state } = useConfirmation()

  if (state !== 'approval-requested') {
    return null
  }

  return <AlertDescription className={cn('text-xs', className)} {...props} />
}

export function ConfirmationAccepted({
  className,
  ...props
}: ComponentProps<'div'>) {
  const { approval, state } = useConfirmation()

  if (!isAccepted(state, approval)) {
    return null
  }

  return (
    <div
      className={cn('text-xs text-emerald-700 dark:text-emerald-300', className)}
      {...props}
    />
  )
}

export function ConfirmationRejected({
  className,
  ...props
}: ComponentProps<'div'>) {
  const { approval, state } = useConfirmation()

  if (!isRejected(state, approval)) {
    return null
  }

  return <div className={cn('text-xs', className)} {...props} />
}

export function ConfirmationActions({
  className,
  ...props
}: ComponentProps<'div'>) {
  const { state } = useConfirmation()

  if (state !== 'approval-requested') {
    return null
  }

  return <div className={cn('mt-3 flex flex-wrap gap-2', className)} {...props} />
}

export function ConfirmationAction({
  className,
  size = 'sm',
  ...props
}: ComponentProps<typeof Button>) {
  return (
    <Button
      className={cn('h-8 px-3 text-xs', className)}
      size={size}
      type="button"
      {...props}
    />
  )
}
